import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToId } from "../hooks/useSmoothScroll";
import { useLang } from "../hooks/useLanguage";

const LangSwitch = ({ className = "", testPrefix = "lang-switch" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid={`${testPrefix}-group`}>
      <button
        onClick={() => setLang("en")}
        className={`text-[0.68rem] font-semibold transition-colors duration-300 ${
          lang === "en" ? "text-champagne" : "text-ivory/50 hover:text-ivory"
        }`}
        aria-pressed={lang === "en"}
        data-testid={`${testPrefix}-en`}
      >
        EN
      </button>
      <span className="text-ivory/25">/</span>
      <button
        onClick={() => setLang("ar")}
        className={`text-[0.72rem] font-semibold transition-colors duration-300 ${
          lang === "ar" ? "text-champagne" : "text-ivory/50 hover:text-ivory"
        }`}
        aria-pressed={lang === "ar"}
        data-testid={`${testPrefix}-ar`}
      >
        عربي
      </button>
    </div>
  );
};

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useLang();
  const home = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (id) => {
    setOpen(false);
    if (home) scrollToId(id);
    else window.location.href = `/#${id}`;
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-500 ${
          scrolled || open
            ? "bg-[#141414]/85 backdrop-blur-md border-b hairline-dark"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="main-nav"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-10 flex items-center justify-between h-[4.5rem]">
          <a
            href={home ? "#top" : "/"}
            onClick={(e) => {
              if (home) {
                e.preventDefault();
                go("top");
              }
            }}
            className="latin-text font-serif-lux text-lg sm:text-xl font-light tracking-[0.32em] text-ivory uppercase"
            data-testid="nav-wordmark"
            aria-label={t.UI.wordmarkAlt}
          >
            LE&nbsp;Capital
          </a>
          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {t.NAV_LINKS.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  go(l.id);
                }}
                className="text-[0.68rem] font-medium uppercase tracking-[0.22em] text-ivory/70 hover:text-champagne transition-colors duration-300"
                data-testid={`nav-link-${l.id}`}
              >
                {l.label}
              </a>
            ))}
            <LangSwitch />
            <button
              onClick={() => go("consultation")}
              className="btn-lux btn-lux-outline !py-3 !px-6"
              data-testid="nav-cta"
            >
              {t.UI.navCta}
            </button>
          </nav>
          <div className="lg:hidden flex items-center gap-4">
            <LangSwitch />
            <button
              className="text-ivory p-2 -me-2"
              onClick={() => setOpen(!open)}
              aria-label={open ? t.UI.menuClose : t.UI.menuOpen}
              aria-expanded={open}
              data-testid="nav-menu-button"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-40 bg-[#141414] pt-28 px-8 flex flex-col lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="mobile-menu"
          >
            {t.NAV_LINKS.map((l, i) => (
              <motion.button
                key={l.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + i * 0.06, duration: 0.5 }}
                onClick={() => go(l.id)}
                className="text-start font-serif-lux text-3xl sm:text-4xl font-light text-ivory py-5 border-b hairline-dark"
                data-testid={`mobile-nav-link-${l.id}`}
              >
                {l.label}
              </motion.button>
            ))}
            <motion.button
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.42, duration: 0.5 }}
              onClick={() => go("consultation")}
              className="btn-lux btn-lux-solid mt-10 self-start"
              data-testid="mobile-nav-cta"
            >
              {t.UI.navCta}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
